import { formatPhoneInput } from '@protoss/shared';
import type { TabsItem } from '@protoss/ui';
import {
  Button,
  CheckIcon,
  IconSize,
  Pencil1Icon,
  Pill,
  ReloadIcon,
  RichTextEditor,
  Select,
  SelectItem,
  SkeletonBlock,
  SkeletonCircle,
  SkeletonText,
  Tabs,
  TagInput,
  TextArea,
  type PillVariant,
} from '@protoss/ui';
import * as React from 'react';
import { ResizableDivider } from '../../../components/ResizableDivider';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { AiProvenanceBadge } from '../../../components/shared/sales';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { formatDateTimeWithYear } from '../../../lib/datetime';
import { toExternalUrl } from '../../../lib/url';
import { useCustomerDetail } from '../hooks/useCustomerDetail';
import type { CustomerDetailRecord, CustomerUpdatePayload } from '../services/customersApi';
import { customersApi } from '../services/customersApi';
import { CustomerAvatar } from './CustomerAvatar';
import { CustomerForm, type CustomerImageSubmission } from './CustomerForm';
import type { NewAttributeDraft } from './CustomerRightRailPanels';

function customerStatusVariant(status: string): PillVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'archived':
      return 'danger';
    default:
      return 'neutral';
  }
}

interface CustomerDetailPageProps {
  projectUuid?: string;
  customerUuid: string;
  canEdit: boolean;
  /** Controlled editing state - owned by the parent so header buttons can drive it. Defaults to local state when not provided. */
  editing?: boolean;
  onEndEdit?: () => void;
  onStartEdit?: () => void;
  formId?: string;
  hideActions?: boolean;
  onFormStateChange?: (state: { submitting: boolean; canSubmit: boolean }) => void;
  /** Called when the customer edit form saves successfully. Parent is responsible for timing and navigation. */
  onSaveSucceeded?: () => void;
  /** Called when the customer record loads or its name changes, so the parent can update the page title. */
  onCustomerNameChange?: (name: string | null) => void;
  pendingAttributeDraft?: NewAttributeDraft | null;
  onPendingAttributeConsumed?: () => void;
  deletedAttributeSignal?: { fieldKey: string; name: string; nonce: number } | null;
  /** Increment to force a reload of customer data from the server (e.g. after external writes like extraction apply). */
  reloadSignal?: number;
}

const DETAIL_TABS = [
  { id: 'attributes', fallbackLabel: 'Behaviors' },
  { id: 'persona', fallbackLabel: 'Goals' },
  { id: 'voice', fallbackLabel: 'Voice' },
  { id: 'empathy', fallbackLabel: 'Empathy Map' },
  { id: 'activity', fallbackLabel: 'Notes' },
] as const;

type CustomerDetailTab = (typeof DETAIL_TABS)[number]['id'];

const CUSTOMER_NOTES_FIELD_KEY = 'notes';

interface CustomerNotesValue {
  doc: Record<string, unknown> | null;
  text: string;
}

function normalizeNotesPayload(
  doc: Record<string, unknown> | null,
  text: string
): { doc: Record<string, unknown> | null; text: string } | null {
  const normalizedText = normalizeNotesText(text);
  if (!normalizedText) {
    return null;
  }

  return {
    doc,
    text: normalizedText,
  };
}

function normalizeNotesText(value: string): string {
  return value.replace(/\r\n?/g, '\n').trim();
}

function autoGrowTextarea(target: HTMLTextAreaElement): void {
  target.style.height = 'auto';
  target.style.height = `${target.scrollHeight}px`;
}

function parseCustomerNotesValue(value: unknown): CustomerNotesValue {
  if (typeof value === 'string') {
    return { doc: null, text: value };
  }
  if (value && typeof value === 'object') {
    const record = value as { doc?: unknown; text?: unknown };
    return {
      doc:
        record.doc && typeof record.doc === 'object' && !Array.isArray(record.doc)
          ? (record.doc as Record<string, unknown>)
          : null,
      text: typeof record.text === 'string' ? record.text : '',
    };
  }
  return { doc: null, text: '' };
}

function isCustomerDetailTab(value: string): value is CustomerDetailTab {
  return DETAIL_TABS.some((tab) => tab.id === value);
}

function resolveTemplateSectionLabel(
  sections: NonNullable<ReturnType<typeof useCustomerDetail>['customer']>['template']['sections'],
  sectionKey: string,
  fallback: string
): string {
  return sections.find((section) => section.sectionKey === sectionKey)?.label ?? fallback;
}

function formatAttributeValue(
  value: NonNullable<
    ReturnType<typeof useCustomerDetail>['customer']
  >['attributes'][number]['value']
): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return 'No value yet';
  return String(value);
}

function hasAttributeValue(
  value: NonNullable<
    ReturnType<typeof useCustomerDetail>['customer']
  >['attributes'][number]['value']
): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return !Number.isNaN(value);
  return value !== null && value !== undefined && String(value).trim() !== '';
}

type _TemplateAttr = CustomerDetailRecord['template']['mergedAttributes'][number];

interface SectionItem {
  key: string;
  label: string;
  description: string | null;
  fieldType: _TemplateAttr['fieldType'];
  control: _TemplateAttr['control'];
  aiEnabled: boolean;
  displayOrder: number;
  value: string | string[];
  valueState: string | null;
  confidenceScore: number | null;
}

/**
 * Build the section item array for a given sectionKey, merging template attribute definitions
 * with stored customer values. Used by the per-tab useMemo blocks to avoid duplicated logic.
 */
function buildSectionItems(customer: CustomerDetailRecord, sectionKey: string): SectionItem[] {
  const templateAttributes = customer.template.mergedAttributes
    .filter((a) => a.sectionKey === sectionKey)
    .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));

  const valueByFieldKey = new Map(
    customer.attributes.filter((a) => a.sectionKey === sectionKey).map((a) => [a.fieldKey, a])
  );

  if (templateAttributes.length > 0) {
    return templateAttributes.map((templateAttribute) => {
      const valueField = valueByFieldKey.get(templateAttribute.fieldKey);
      const rawValue = valueField?.value ?? null;
      const isMulti =
        templateAttribute.control === 'tags' || templateAttribute.control === 'multi_select';
      const formattedValue: string | string[] =
        valueField && hasAttributeValue(rawValue)
          ? isMulti
            ? Array.isArray(rawValue)
              ? rawValue.map(String)
              : String(rawValue)
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
            : formatAttributeValue(rawValue)
          : isMulti
            ? []
            : '';
      return {
        key: templateAttribute.fieldKey,
        label: templateAttribute.name,
        description: templateAttribute.description?.trim() || null,
        fieldType: templateAttribute.fieldType,
        control: templateAttribute.control,
        aiEnabled: templateAttribute.aiEnabled,
        displayOrder: templateAttribute.displayOrder,
        value: formattedValue,
        valueState: valueField?.valueState ?? null,
        confidenceScore: valueField?.confidenceScore ?? null,
      };
    });
  }

  return customer.attributes
    .filter((a) => a.sectionKey === sectionKey)
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
    .map((attribute) => ({
      key: attribute.fieldKey,
      label: attribute.name,
      description: attribute.description?.trim() || null,
      fieldType: attribute.fieldType,
      control: 'textarea' as _TemplateAttr['control'],
      aiEnabled: attribute.aiEnabled,
      displayOrder: attribute.displayOrder,
      value: (hasAttributeValue(attribute.value) ? formatAttributeValue(attribute.value) : '') as
        | string
        | string[],
      valueState: attribute.valueState ?? null,
      confidenceScore: attribute.confidenceScore ?? null,
    }));
}

/** Clears a save-success flag after 2.4 s, preventing stale success state from persisting. */
function useSaveSuccessFlash(
  succeeded: boolean,
  clearSucceeded: React.Dispatch<React.SetStateAction<boolean>>
): void {
  React.useEffect(() => {
    if (!succeeded) return;
    const timer = window.setTimeout(() => clearSucceeded(false), 2400);
    return () => window.clearTimeout(timer);
  }, [succeeded, clearSucceeded]);
}

/**
 * CustomerDetailPage - customer-first detail surface with dynamic attributes.
 */
export function CustomerDetailPage({
  projectUuid,
  customerUuid,
  canEdit,
  editing: editingProp,
  onEndEdit,
  onStartEdit,
  formId,
  hideActions,
  onFormStateChange,
  onSaveSucceeded,
  onCustomerNameChange,
  pendingAttributeDraft,
  onPendingAttributeConsumed,
  deletedAttributeSignal,
  reloadSignal,
}: CustomerDetailPageProps) {
  const { workspace } = useWorkspace();
  const aiProvenanceEnabled = workspace?.aiProvenanceEnabled ?? false;
  const { customer, loading, error, reload, setCustomer } = useCustomerDetail(
    projectUuid,
    customerUuid
  );

  // Incremented after an external reload completes so draft-init effects can reinitialise.
  const [reinitKey, setReinitKey] = React.useState(0);
  React.useEffect(() => {
    if (!reloadSignal) return;
    void reload().then(() => setReinitKey((k) => k + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadSignal]);

  React.useEffect(() => {
    onCustomerNameChange?.(customer?.name ?? null);
  }, [customer?.name, onCustomerNameChange]);

  const [activeTab, setActiveTab] = React.useState<CustomerDetailTab>('attributes');
  // Allow uncontrolled usage (e.g. ProjectPage) by falling back to local state
  const [localEditing, setLocalEditing] = React.useState(false);
  const editing = editingProp ?? localEditing;
  const handleEndEdit = onEndEdit ?? (() => setLocalEditing(false));
  const [saveError, setSaveError] = React.useState<string | null>(null);
  // Bumped after each successful update so CustomerForm resets its dirty-state snapshot.
  const [saveCount, setSaveCount] = React.useState(0);
  const [notesBody, setNotesBody] = React.useState<Record<string, unknown> | null>(null);
  const [notesText, setNotesText] = React.useState('');
  const [notesSaving, setNotesSaving] = React.useState(false);
  const [notesSaveSucceeded, setNotesSaveSucceeded] = React.useState(false);
  const [notesSaveError, setNotesSaveError] = React.useState<string | null>(null);
  const [behaviorDraftValues, setBehaviorDraftValues] = React.useState<Record<string, string[]>>(
    {}
  );
  const [behaviorSaving, setBehaviorSaving] = React.useState(false);
  const [behaviorSaveSucceeded, setBehaviorSaveSucceeded] = React.useState(false);
  const [behaviorSaveError, setBehaviorSaveError] = React.useState<string | null>(null);
  const [goalDraftValues, setGoalDraftValues] = React.useState<Record<string, string>>({});
  const [goalSaving, setGoalSaving] = React.useState(false);
  const [goalSaveSucceeded, setGoalSaveSucceeded] = React.useState(false);
  const [goalSaveError, setGoalSaveError] = React.useState<string | null>(null);
  const [empathyDraftValues, setEmpathyDraftValues] = React.useState<Record<string, string>>({});
  const [empathySaving, setEmpathySaving] = React.useState(false);
  const [empathySaveSucceeded, setEmpathySaveSucceeded] = React.useState(false);
  const [empathySaveError, setEmpathySaveError] = React.useState<string | null>(null);
  const [voiceDraftValues, setVoiceDraftValues] = React.useState<Record<string, string[]>>({});
  const [voiceSaving, setVoiceSaving] = React.useState(false);
  const [voiceSaveSucceeded, setVoiceSaveSucceeded] = React.useState(false);
  const [voiceSaveError, setVoiceSaveError] = React.useState<string | null>(null);

  const handleTabChange = React.useCallback((value: string) => {
    if (isCustomerDetailTab(value)) {
      setActiveTab(value);
    }
  }, []);

  const [identityWidth, setIdentityWidth] = React.useState(300);
  const handleSplitResize = React.useCallback((delta: number) => {
    setIdentityWidth((prev) => Math.max(220, Math.min(400, prev + delta)));
  }, []);

  // ── Responsive split layout ───────────────────────────────────────────
  // Observe the *stable* flex parent (.customer-detail__split) - its width
  // is set by the CSS grid column and does NOT change when identity
  // collapses/expands.  We measure the right child (.customer-detail__split-right)
  // for breakpoint checks because the thresholds are calibrated against it.
  //
  // Identity collapse is ONE-WAY (only auto-collapse, never auto-expand).
  // Expanding identity takes ~300px from split-right, which would change
  // the measurement and re-trigger collapse - a feedback loop.
  // Identity only re-expands via explicit user click on the expand button.
  //
  // Tabs overflow is BIDIRECTIONAL (safe) - tabs don't change container
  // width, so no feedback loop is possible.

  const IDENTITY_BP = 480; // px: below this, identity pane collapses
  const DROPDOWN_BP = 356; // px: below this, tabs switch to dropdown

  const [identityCollapsed, setIdentityCollapsed] = React.useState(false);
  const [tabsOverflow, setTabsOverflow] = React.useState(false);
  const { copied: copiedEmail, copy: copyEmail } = useCopyToClipboard();
  const [splitContainerEl, setSplitContainerEl] = React.useState<HTMLDivElement | null>(null);
  const splitContainerCallbackRef = React.useCallback((el: HTMLDivElement | null) => {
    setSplitContainerEl(el);
  }, []);
  const splitRightRef = React.useRef<HTMLDivElement>(null);
  const tabsWrapperRef = React.useRef<HTMLDivElement>(null);
  const userExpandedIdentityRef = React.useRef(false);

  React.useEffect(() => {
    if (!splitContainerEl) return;
    const observer = new ResizeObserver(() => {
      const w = splitRightRef.current?.getBoundingClientRect().width ?? Infinity;
      // Tabs overflow: bidirectional - safe because tabs don't affect container width
      setTabsOverflow(w < DROPDOWN_BP);
      // Identity collapse: one-way only - auto-collapse when narrow, never auto-expand
      if (w < IDENTITY_BP && !userExpandedIdentityRef.current) setIdentityCollapsed(true);
    });
    observer.observe(splitContainerEl);
    return () => observer.disconnect();
  }, [splitContainerEl]);

  React.useEffect(() => {
    setActiveTab('attributes');
    setLocalEditing(false);
    setSaveError(null);
  }, [customerUuid]);

  // Clear save error whenever editing is cancelled from outside
  React.useEffect(() => {
    if (!editing) setSaveError(null);
  }, [editing]);

  const notesAttribute = React.useMemo(
    () =>
      customer?.attributes.find((attribute) => attribute.fieldKey === CUSTOMER_NOTES_FIELD_KEY) ??
      null,
    [customer]
  );

  const notesTemplateAttribute = React.useMemo(
    () =>
      customer?.template.mergedAttributes.find(
        (attribute) => attribute.fieldKey === CUSTOMER_NOTES_FIELD_KEY
      ) ?? null,
    [customer]
  );

  const persistedNotes = React.useMemo(
    () => parseCustomerNotesValue(notesAttribute?.value ?? null),
    [notesAttribute?.value]
  );

  React.useEffect(() => {
    setNotesBody(persistedNotes.doc);
    setNotesText(persistedNotes.text);
    setNotesSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.uuid, reinitKey]);

  React.useEffect(() => {
    setNotesSaveSucceeded(false);
    setNotesSaveError(null);
  }, [customerUuid]);

  useSaveSuccessFlash(notesSaveSucceeded, setNotesSaveSucceeded);

  const notesDirty = React.useMemo(
    () => normalizeNotesText(notesText) !== normalizeNotesText(persistedNotes.text),
    [notesText, persistedNotes.text]
  );

  const behaviorSections = React.useMemo(
    () => (customer ? buildSectionItems(customer, 'behaviors') : []),
    [customer]
  );

  const goalSections = React.useMemo(
    () => (customer ? buildSectionItems(customer, 'goals') : []),
    [customer]
  );

  const empathySections = React.useMemo(
    () => (customer ? buildSectionItems(customer, 'empathy_map') : []),
    [customer]
  );

  const voiceSections = React.useMemo(
    () => (customer ? buildSectionItems(customer, 'voice') : []),
    [customer]
  );

  React.useEffect(() => {
    setBehaviorDraftValues(
      Object.fromEntries(
        behaviorSections.map((section) => [
          section.key,
          Array.isArray(section.value) ? section.value : [section.value].filter(Boolean),
        ])
      )
    );
    setBehaviorSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.uuid, reinitKey]);

  React.useEffect(() => {
    setBehaviorSaveSucceeded(false);
    setBehaviorSaveError(null);
  }, [customerUuid]);

  React.useEffect(() => {
    setGoalDraftValues(
      Object.fromEntries(goalSections.map((section) => [section.key, section.value as string]))
    );
    setGoalSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.uuid, reinitKey]);

  React.useEffect(() => {
    setGoalSaveSucceeded(false);
    setGoalSaveError(null);
  }, [customerUuid]);

  React.useEffect(() => {
    setEmpathyDraftValues(
      Object.fromEntries(empathySections.map((section) => [section.key, section.value as string]))
    );
    setEmpathySaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.uuid, reinitKey]);

  React.useEffect(() => {
    setEmpathySaveSucceeded(false);
    setEmpathySaveError(null);
  }, [customerUuid]);

  React.useEffect(() => {
    // Voice draft values are keyed the same way but store string[] for tags fields and string for text/textarea
    setVoiceDraftValues(
      Object.fromEntries(
        voiceSections.map((section) => [
          section.key,
          Array.isArray(section.value) ? section.value : [section.value].filter(Boolean),
        ])
      )
    );
    setVoiceSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.uuid, reinitKey]);

  React.useEffect(() => {
    setVoiceSaveSucceeded(false);
    setVoiceSaveError(null);
  }, [customerUuid]);

  useSaveSuccessFlash(behaviorSaveSucceeded, setBehaviorSaveSucceeded);
  useSaveSuccessFlash(goalSaveSucceeded, setGoalSaveSucceeded);
  useSaveSuccessFlash(empathySaveSucceeded, setEmpathySaveSucceeded);
  useSaveSuccessFlash(voiceSaveSucceeded, setVoiceSaveSucceeded);

  const behaviorDirtyKeys = React.useMemo(
    () =>
      behaviorSections
        .filter((section) => {
          const isMulti = section.control === 'tags' || section.control === 'multi_select';
          if (isMulti) {
            const draft = behaviorDraftValues[section.key] ?? [];
            const persisted = Array.isArray(section.value)
              ? section.value
              : [section.value].filter(Boolean);
            return [...draft].sort().join(',') !== [...persisted].sort().join(',');
          }
          return (
            normalizeNotesText((behaviorDraftValues[section.key] ?? []).join('')) !==
            normalizeNotesText(
              Array.isArray(section.value) ? section.value.join('') : section.value
            )
          );
        })
        .map((section) => section.key),
    [behaviorDraftValues, behaviorSections]
  );

  const behaviorDirtyKeySet = React.useMemo(() => new Set(behaviorDirtyKeys), [behaviorDirtyKeys]);
  const behaviorDirty = behaviorDirtyKeys.length > 0;

  const goalDirtyKeys = React.useMemo(
    () =>
      goalSections
        .filter(
          (section) =>
            normalizeNotesText(goalDraftValues[section.key] ?? '') !==
            normalizeNotesText(section.value as string)
        )
        .map((section) => section.key),
    [goalDraftValues, goalSections]
  );

  const goalDirtyKeySet = React.useMemo(() => new Set(goalDirtyKeys), [goalDirtyKeys]);
  const goalDirty = goalDirtyKeys.length > 0;

  const empathyDirtyKeys = React.useMemo(
    () =>
      empathySections
        .filter(
          (section) =>
            normalizeNotesText(empathyDraftValues[section.key] ?? '') !==
            normalizeNotesText(section.value as string)
        )
        .map((section) => section.key),
    [empathyDraftValues, empathySections]
  );

  const empathyDirtyKeySet = React.useMemo(() => new Set(empathyDirtyKeys), [empathyDirtyKeys]);
  const empathyDirty = empathyDirtyKeys.length > 0;

  const voiceDirtyKeys = React.useMemo(
    () =>
      voiceSections
        .filter((section) => {
          const draft = voiceDraftValues[section.key] ?? [];
          const persisted = Array.isArray(section.value)
            ? section.value
            : [section.value].filter(Boolean);
          return [...draft].sort().join(',') !== [...persisted].sort().join(',');
        })
        .map((section) => section.key),
    [voiceDraftValues, voiceSections]
  );

  const voiceDirtyKeySet = React.useMemo(() => new Set(voiceDirtyKeys), [voiceDirtyKeys]);
  const voiceDirty = voiceDirtyKeys.length > 0;

  useUnsavedChanges(behaviorDirty || goalDirty || empathyDirty || voiceDirty || notesDirty);

  const handleBehaviorSave = React.useCallback(async () => {
    if (!customer || behaviorSaving || !behaviorDirty) return;

    const updates = behaviorSections.map((section) => {
      const isMulti = section.control === 'tags' || section.control === 'multi_select';
      const draft = behaviorDraftValues[section.key] ?? [];
      const nextValue = isMulti
        ? draft.length > 0
          ? draft
          : null
        : normalizeNotesText(draft.join('')) || null;

      return {
        fieldKey: section.key,
        name: section.label,
        fieldType: section.fieldType,
        control: section.control,
        sectionKey: 'behaviors',
        aiEnabled: section.aiEnabled,
        description: section.description,
        value: nextValue,
        saveToUserTemplate: false,
      };
    });

    setBehaviorSaving(true);
    setBehaviorSaveError(null);
    setBehaviorSaveSucceeded(false);

    try {
      const { customer: updated } = await customersApi.updateCustomer(projectUuid, customerUuid, {
        attributes: updates,
      });

      setCustomer(updated);
      setBehaviorSaveSucceeded(true);
    } catch (err: unknown) {
      setBehaviorSaveError((err as Error).message);
    } finally {
      setBehaviorSaving(false);
    }
  }, [
    customer,
    behaviorSaving,
    behaviorDirty,
    behaviorSections,
    behaviorDraftValues,
    projectUuid,
    customerUuid,
    setCustomer,
  ]);

  const handleGoalSave = React.useCallback(async () => {
    if (!customer || goalSaving || !goalDirty) return;

    const updates = goalSections.map((section) => {
      const normalizedValue = normalizeNotesText(goalDraftValues[section.key] ?? '');
      const nextValue = normalizedValue || null;

      return {
        fieldKey: section.key,
        name: section.label,
        fieldType: section.fieldType,
        control: section.control,
        sectionKey: 'goals',
        aiEnabled: section.aiEnabled,
        description: section.description,
        value: nextValue,
        saveToUserTemplate: false,
      };
    });

    setGoalSaving(true);
    setGoalSaveError(null);
    setGoalSaveSucceeded(false);

    try {
      const { customer: updated } = await customersApi.updateCustomer(projectUuid, customerUuid, {
        attributes: updates,
      });

      setCustomer(updated);
      setGoalSaveSucceeded(true);
    } catch (err: unknown) {
      setGoalSaveError((err as Error).message);
    } finally {
      setGoalSaving(false);
    }
  }, [
    customer,
    goalSaving,
    goalDirty,
    goalSections,
    goalDraftValues,
    projectUuid,
    customerUuid,
    setCustomer,
  ]);

  const handleEmpathySave = React.useCallback(async () => {
    if (!customer || empathySaving || !empathyDirty) return;

    const updates = empathySections.map((section) => {
      const normalizedValue = normalizeNotesText(empathyDraftValues[section.key] ?? '');
      const nextValue = normalizedValue || null;

      return {
        fieldKey: section.key,
        name: section.label,
        fieldType: section.fieldType,
        control: section.control,
        sectionKey: 'empathy_map',
        aiEnabled: section.aiEnabled,
        description: section.description,
        value: nextValue,
        saveToUserTemplate: false,
      };
    });

    setEmpathySaving(true);
    setEmpathySaveError(null);
    setEmpathySaveSucceeded(false);

    try {
      const { customer: updated } = await customersApi.updateCustomer(projectUuid, customerUuid, {
        attributes: updates,
      });

      setCustomer(updated);
      setEmpathySaveSucceeded(true);
    } catch (err: unknown) {
      setEmpathySaveError((err as Error).message);
    } finally {
      setEmpathySaving(false);
    }
  }, [
    customer,
    empathySaving,
    empathyDirty,
    empathySections,
    empathyDraftValues,
    projectUuid,
    customerUuid,
    setCustomer,
  ]);

  const handleVoiceSave = React.useCallback(async () => {
    if (!customer || voiceSaving || !voiceDirty) return;

    const updates = voiceSections.map((section) => {
      const isMulti = section.control === 'tags' || section.control === 'multi_select';
      const draft = voiceDraftValues[section.key] ?? [];
      const nextValue = isMulti
        ? draft.length > 0
          ? draft
          : null
        : normalizeNotesText(draft[0] ?? '') || null;

      return {
        fieldKey: section.key,
        name: section.label,
        fieldType: section.fieldType,
        control: section.control,
        sectionKey: 'voice',
        aiEnabled: section.aiEnabled,
        description: section.description,
        value: nextValue,
        saveToUserTemplate: false,
      };
    });

    setVoiceSaving(true);
    setVoiceSaveError(null);
    setVoiceSaveSucceeded(false);

    try {
      const { customer: updated } = await customersApi.updateCustomer(projectUuid, customerUuid, {
        attributes: updates,
      });
      setCustomer(updated);
      setVoiceSaveSucceeded(true);
    } catch (err: unknown) {
      setVoiceSaveError((err as Error).message);
    } finally {
      setVoiceSaving(false);
    }
  }, [
    customer,
    voiceSaving,
    voiceDirty,
    voiceSections,
    voiceDraftValues,
    projectUuid,
    customerUuid,
    setCustomer,
  ]);

  const handleNotesSave = React.useCallback(async () => {
    if (!customer || notesSaving || !notesDirty) return;
    setNotesSaving(true);
    setNotesSaveError(null);
    setNotesSaveSucceeded(false);

    const nextValue = normalizeNotesPayload(notesBody, notesText);

    try {
      const { customer: updated } = await customersApi.updateCustomer(projectUuid, customerUuid, {
        attributes: [
          {
            fieldKey: CUSTOMER_NOTES_FIELD_KEY,
            name: notesTemplateAttribute?.name ?? 'Notes',
            fieldType: notesTemplateAttribute?.fieldType ?? 'long_text',
            control: notesTemplateAttribute?.control ?? 'textarea',
            sectionKey: 'notes',
            aiEnabled: notesTemplateAttribute?.aiEnabled ?? false,
            value: nextValue,
            saveToUserTemplate: false,
          },
        ],
      });

      setCustomer(updated);
      setNotesSaveSucceeded(true);
    } catch (err: unknown) {
      setNotesSaveError((err as Error).message);
    } finally {
      setNotesSaving(false);
    }
  }, [
    customer,
    notesSaving,
    notesDirty,
    notesText,
    notesBody,
    notesTemplateAttribute,
    projectUuid,
    customerUuid,
    setCustomer,
  ]);

  const handleTabCancel = React.useCallback(() => {
    if (activeTab === 'attributes') {
      setBehaviorDraftValues(
        Object.fromEntries(
          behaviorSections.map((section) => [
            section.key,
            Array.isArray(section.value) ? section.value : [section.value].filter(Boolean),
          ])
        )
      );
      setBehaviorSaveError(null);
    } else if (activeTab === 'persona') {
      setGoalDraftValues(
        Object.fromEntries(goalSections.map((section) => [section.key, section.value as string]))
      );
      setGoalSaveError(null);
    } else if (activeTab === 'voice') {
      setVoiceDraftValues(
        Object.fromEntries(
          voiceSections.map((section) => [
            section.key,
            Array.isArray(section.value) ? section.value : [section.value].filter(Boolean),
          ])
        )
      );
      setVoiceSaveError(null);
    } else if (activeTab === 'empathy') {
      setEmpathyDraftValues(
        Object.fromEntries(empathySections.map((section) => [section.key, section.value as string]))
      );
      setEmpathySaveError(null);
    } else if (activeTab === 'activity') {
      setNotesBody(persistedNotes.doc);
      setNotesText(persistedNotes.text);
      setNotesSaveError(null);
    }
  }, [activeTab, behaviorSections, goalSections, voiceSections, empathySections, persistedNotes]);

  const handleUpdate = async (data: CustomerUpdatePayload, image: CustomerImageSubmission) => {
    setSaveError(null);
    try {
      const { customer: updated } = await customersApi.updateCustomer(
        projectUuid,
        customerUuid,
        data
      );

      if (image.removeImage) {
        await customersApi.removeCustomerImage(projectUuid, customerUuid);
        await reload();
      } else if (image.imageFile) {
        await customersApi.uploadCustomerImage(projectUuid, customerUuid, image.imageFile);
        await reload();
      } else {
        setCustomer(updated);
      }

      setSaveCount((n) => n + 1);
      if (onSaveSucceeded) {
        onSaveSucceeded();
      } else {
        handleEndEdit();
      }
    } catch (err: unknown) {
      setSaveError((err as Error).message);
      throw err;
    }
  };

  if (loading && !customer) {
    return (
      <div className="customer-detail" aria-busy="true">
        <div className="customer-detail__header">
          <div className="customer-detail__header-info">
            <SkeletonCircle
              className="customer-detail__avatar customer-detail__avatar-skeleton"
              size="72px"
            />
            <div className="customer-detail__header-copy">
              <div className="customer-detail__name-row">
                <h2 className="customer-detail__name">
                  <SkeletonText width="180px">Customer</SkeletonText>
                </h2>
                <SkeletonBlock className="customer-detail__contact-skeleton" />
              </div>
              <div className="customer-detail__header-pills">
                <SkeletonBlock className="customer-detail__pill-skeleton" />
                <SkeletonBlock className="customer-detail__pill-skeleton customer-detail__pill-skeleton--secondary" />
              </div>
            </div>
          </div>
          <div
            className="customer-detail__header-meta customer-detail__header-meta--compact"
            aria-label="Customer timestamps"
          >
            <div className="customer-detail__meta-row">
              <span className="customer-detail__meta-label">
                <SkeletonText width="52px">Created</SkeletonText>
              </span>
              <span className="customer-detail__meta-value">
                <SkeletonText width="112px">Timestamp</SkeletonText>
              </span>
            </div>
            <div className="customer-detail__meta-row">
              <span className="customer-detail__meta-label">
                <SkeletonText width="80px">Last updated</SkeletonText>
              </span>
              <span className="customer-detail__meta-value">
                <SkeletonText width="112px">Timestamp</SkeletonText>
              </span>
            </div>
          </div>
        </div>

        <div className="customer-detail__split">
          <div className="customer-detail__split-left">
            <section className="customer-detail__section">
              <div className="customer-detail__section-heading customer-detail__section-heading--with-action">
                <h3 className="customer-detail__section-title">
                  <SkeletonText width="96px">Identity</SkeletonText>
                </h3>
                <SkeletonBlock className="customer-detail__edit-skeleton" />
              </div>
              <div className="customer-profile">
                <div className="customer-profile__field">
                  <span className="customer-profile__label">
                    <SkeletonText width="84px">Company</SkeletonText>
                  </span>
                  <SkeletonBlock className="customer-detail__field-skeleton" />
                </div>
                <div className="customer-profile__field">
                  <span className="customer-profile__label">
                    <SkeletonText width="72px">Role</SkeletonText>
                  </span>
                  <SkeletonBlock className="customer-detail__field-skeleton" />
                </div>
                <div className="customer-profile__field">
                  <span className="customer-profile__label">
                    <SkeletonText width="64px">Region</SkeletonText>
                  </span>
                  <SkeletonBlock className="customer-detail__field-skeleton" />
                </div>
              </div>
            </section>
          </div>

          <ResizableDivider direction="vertical" disabled ariaLabel="Resize identity pane" />

          <div className="customer-detail__split-right">
            <div className="customer-detail__split-tabs-header">
              <div className="customer-detail__split-tabs customer-detail__split-tabs--skeleton">
                <SkeletonBlock className="customer-detail__tab-skeleton" />
                <SkeletonBlock className="customer-detail__tab-skeleton" />
                <SkeletonBlock className="customer-detail__tab-skeleton" />
              </div>
              <div className="customer-detail__split-tabs-actions">
                <SkeletonBlock className="customer-detail__tab-action-skeleton customer-detail__tab-action-skeleton--secondary" />
                <SkeletonBlock className="customer-detail__tab-action-skeleton" />
              </div>
            </div>

            <div className="customer-detail__split-tab-content">
              <div className="customer-detail__group-sections">
                <section className="customer-detail__group-section">
                  <div className="customer-detail__section-heading customer-detail__section-heading--inline-copy">
                    <h3 className="customer-detail__section-title">
                      <SkeletonText width="120px">Behavior</SkeletonText>
                    </h3>
                    <p className="customer-detail__section-copy customer-detail__section-copy--inline">
                      <SkeletonText width="180px">Placeholder copy</SkeletonText>
                    </p>
                  </div>
                  <SkeletonBlock className="customer-detail__textarea-skeleton" />
                </section>
                <section className="customer-detail__group-section">
                  <div className="customer-detail__section-heading customer-detail__section-heading--inline-copy">
                    <h3 className="customer-detail__section-title">
                      <SkeletonText width="132px">Goals</SkeletonText>
                    </h3>
                    <p className="customer-detail__section-copy customer-detail__section-copy--inline">
                      <SkeletonText width="160px">Placeholder copy</SkeletonText>
                    </p>
                  </div>
                  <SkeletonBlock className="customer-detail__textarea-skeleton" />
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <PageErrorState message={error} onRetry={reload} />;
  }

  if (!customer) return null;

  // Edit mode: plain form without the customer info header
  if (editing) {
    return (
      <div className="customer-list__create-panel">
        {saveError && <p className="customer-detail__save-error">{saveError}</p>}
        <CustomerForm
          projectUuid={projectUuid}
          initial={customer}
          onSubmit={handleUpdate}
          onCancel={handleEndEdit}
          submitLabel="Update customer"
          formId={formId}
          hideActions={hideActions}
          onFormStateChange={onFormStateChange}
          snapshotKey={saveCount}
          pendingAttributeDraft={pendingAttributeDraft}
          onPendingAttributeConsumed={onPendingAttributeConsumed}
          deletedAttributeSignal={deletedAttributeSignal}
          onAttributeValueDeleted={async () => {
            await reload();
            setSaveCount((n) => n + 1);
          }}
        />
      </div>
    );
  }

  const sectionKeyByTab: Record<CustomerDetailTab, string> = {
    attributes: 'behaviors',
    persona: 'goals',
    voice: 'voice',
    empathy: 'empathy_map',
    activity: 'notes',
  };

  const detailTabItems = DETAIL_TABS.map((tab) => {
    const baseLabel = resolveTemplateSectionLabel(
      customer.template.sections,
      sectionKeyByTab[tab.id],
      tab.fallbackLabel
    );

    const hasUnsavedChanges =
      (tab.id === 'activity' && notesDirty) ||
      (tab.id === 'attributes' && behaviorDirty) ||
      (tab.id === 'persona' && goalDirty) ||
      (tab.id === 'voice' && voiceDirty) ||
      (tab.id === 'empathy' && empathyDirty);

    return {
      value: tab.id,
      label: hasUnsavedChanges ? `${baseLabel}*` : baseLabel,
    };
  }) satisfies readonly TabsItem[];

  const statusLabel = customer.status ?? 'unknown';
  const statusVariant = customerStatusVariant(statusLabel);

  const groupedAttributes = {
    basic: customer.attributes.filter((attribute) => attribute.sectionKey === 'identity'),
    behaviors: customer.attributes.filter((attribute) => attribute.sectionKey === 'behaviors'),
    persona: customer.attributes.filter((attribute) => attribute.sectionKey === 'goals'),
    empathy: customer.attributes.filter((attribute) => attribute.sectionKey === 'empathy_map'),
  };
  const reservedIdentityKeys = new Set(['email', 'phone', 'source', 'source_type', 'status']);
  const reservedIdentityLabels = new Set(['email', 'phone', 'source', 'status']);
  const identityFields = [
    ...[...groupedAttributes.basic]
      .filter((attribute) => {
        const fieldKey = attribute.fieldKey.toLowerCase();
        const label = attribute.name.toLowerCase();
        if (reservedIdentityKeys.has(fieldKey) || reservedIdentityLabels.has(label)) return false;
        // Deleted attributes: only show when they have a stored value
        if (attribute.deleted) {
          const formatted = formatAttributeValue(attribute.value);
          return formatted !== 'No value yet' && formatted !== '';
        }
        return true;
      })
      .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
      .map((attribute) => ({
        label: attribute.name,
        value: formatAttributeValue(attribute.value),
        deleted: attribute.deleted ?? false,
      })),
  ];

  const parseTagValues = (value: string): string[] => {
    if (!value || value === 'No value yet') return [];
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  };

  return (
    <div className="customer-detail">
      <div className="customer-detail__header">
        <div className="customer-detail__header-info">
          <CustomerAvatar
            name={customer.name}
            asset={customer.primaryImageAsset}
            size="lg"
            shape="square"
            className="customer-detail__avatar"
          />
          <div className="customer-detail__header-copy">
            <div className="customer-detail__name-row">
              <h2 className="customer-detail__name">{customer.name}</h2>
              {customer.email ? (
                <button
                  type="button"
                  className="customer-detail__contact-email"
                  onClick={() => {
                    void copyEmail(customer.email ?? '');
                  }}
                  aria-label={`Copy email ${customer.email}`}
                >
                  {customer.email}
                  <span className="customer-detail__contact-tooltip" aria-hidden="true">
                    {copiedEmail ? 'Copied' : 'Click to copy'}
                  </span>
                </button>
              ) : null}
              {customer.phone ? (
                <span className="customer-detail__contact-phone">
                  {formatPhoneInput(customer.phone)}
                </span>
              ) : null}
            </div>
            <div className="customer-detail__header-pills">
              <Pill variant={statusVariant}>{statusLabel}</Pill>
              {customer.sourceType ? <Pill variant="ghost">{customer.sourceType}</Pill> : null}
            </div>
          </div>
        </div>
        <div
          className="customer-detail__header-meta customer-detail__header-meta--compact"
          aria-label="Customer timestamps"
        >
          <div className="customer-detail__meta-row">
            <span className="customer-detail__meta-label">Created</span>
            <span className="customer-detail__meta-value">
              {formatDateTimeWithYear(customer.createdAt?.toISOString() ?? null)}
            </span>
          </div>
          <div className="customer-detail__meta-row">
            <span className="customer-detail__meta-label">Last updated</span>
            <span className="customer-detail__meta-value">
              {formatDateTimeWithYear(customer.updatedAt?.toISOString() ?? null)}
            </span>
          </div>
        </div>
      </div>

      <div ref={splitContainerCallbackRef} className="customer-detail__split">
        {/* Left pane: Identity */}
        <div
          className={`customer-detail__split-left${identityCollapsed ? ' customer-detail__split-left--collapsed' : ''}`}
          style={identityCollapsed ? undefined : { width: `${identityWidth}px` }}
        >
          {identityCollapsed ? (
            <button
              type="button"
              className="customer-detail__identity-expand"
              onClick={() => {
                userExpandedIdentityRef.current = true;
                setIdentityCollapsed(false);
              }}
              aria-label="Expand identity pane"
            >
              {/* Single chevron pointing right */}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M6 12l4-4-4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <>
              <section className="customer-detail__section">
                <div className="customer-detail__section-heading customer-detail__section-heading--with-action">
                  <h3 className="customer-detail__section-title">
                    {resolveTemplateSectionLabel(
                      customer.template.sections,
                      'identity',
                      'Identity'
                    )}
                  </h3>
                  {canEdit && onStartEdit && (
                    <button
                      type="button"
                      className="customer-detail__section-edit-btn"
                      aria-label="Edit identity"
                      onClick={onStartEdit}
                    >
                      <Pencil1Icon width={IconSize.md} height={IconSize.md} aria-hidden="true" />
                    </button>
                  )}
                </div>
                <div className="customer-profile">
                  {identityFields.map((field) => (
                    <div
                      key={field.label}
                      className={`customer-profile__field${field.deleted ? ' customer-profile__field--deleted' : ''}`}
                    >
                      <span className="customer-profile__label">
                        {field.label}
                        {field.deleted && (
                          <span className="customer-profile__label-deleted-badge">deleted</span>
                        )}
                      </span>
                      {field.label.toLowerCase() === 'tags'
                        ? (() => {
                            const tags = parseTagValues(field.value);
                            if (tags.length === 0) {
                              return (
                                <span className="customer-profile__value customer-profile__value--muted">
                                  No value yet
                                </span>
                              );
                            }
                            return (
                              <span
                                className="customer-profile__tags"
                                role="list"
                                aria-label="Tags"
                              >
                                {tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="customer-profile__tag-pill"
                                    role="listitem"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </span>
                            );
                          })()
                        : (() => {
                            const href = toExternalUrl(field.value);
                            if (href) {
                              return (
                                <a
                                  className="customer-profile__value customer-profile__value-link"
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {field.value}
                                </a>
                              );
                            }
                            return (
                              <span
                                className={`customer-profile__value${field.value === 'No value yet' ? ' customer-profile__value--muted' : ''}`}
                              >
                                {field.value}
                              </span>
                            );
                          })()}
                    </div>
                  ))}
                </div>
              </section>
              {saveError && <p className="customer-detail__save-error">{saveError}</p>}
            </>
          )}
        </div>

        <ResizableDivider
          direction="vertical"
          onResize={identityCollapsed ? undefined : handleSplitResize}
          onCollapse={identityCollapsed ? undefined : () => setIdentityCollapsed(true)}
          disabled={identityCollapsed}
          ariaLabel="Resize identity pane"
        />

        {/* Right pane: Behaviors, Goals, Empathy Map, Notes */}
        <div ref={splitRightRef} className="customer-detail__split-right">
          <div className="customer-detail__split-tabs-header">
            {/* Wide mode: full tab list - hidden via JS when tabs overflow */}
            <div
              ref={tabsWrapperRef}
              className={`customer-detail__split-tabs${tabsOverflow ? ' customer-detail__split-tabs--hidden' : ''}`}
            >
              <Tabs
                ariaLabel="Customer detail tabs"
                items={detailTabItems}
                value={activeTab}
                onValueChange={handleTabChange}
                variant="subnav"
              />
            </div>
            {/* Narrow mode: compact dropdown - shown via JS when tabs overflow */}
            <div
              className={`customer-detail__tabs-select${tabsOverflow ? ' customer-detail__tabs-select--visible' : ''}`}
            >
              <Select value={activeTab} onValueChange={handleTabChange} size="sm">
                {detailTabItems.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
            {activeTab === 'activity' ||
            activeTab === 'attributes' ||
            activeTab === 'persona' ||
            activeTab === 'voice' ||
            activeTab === 'empathy' ? (
              <div className="customer-detail__split-tabs-actions">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  style={{
                    visibility:
                      (activeTab === 'activity' && notesDirty) ||
                      (activeTab === 'attributes' && behaviorDirty) ||
                      (activeTab === 'persona' && goalDirty) ||
                      (activeTab === 'voice' && voiceDirty) ||
                      (activeTab === 'empathy' && empathyDirty)
                        ? 'visible'
                        : 'hidden',
                  }}
                  onClick={handleTabCancel}
                  aria-label="Cancel changes"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={
                    activeTab === 'activity'
                      ? !notesDirty || notesSaving
                      : activeTab === 'attributes'
                        ? !behaviorDirty || behaviorSaving
                        : activeTab === 'persona'
                          ? !goalDirty || goalSaving
                          : activeTab === 'voice'
                            ? !voiceDirty || voiceSaving
                            : !empathyDirty || empathySaving
                  }
                  onClick={() =>
                    void (activeTab === 'activity'
                      ? handleNotesSave()
                      : activeTab === 'attributes'
                        ? handleBehaviorSave()
                        : activeTab === 'persona'
                          ? handleGoalSave()
                          : activeTab === 'voice'
                            ? handleVoiceSave()
                            : handleEmpathySave())
                  }
                  aria-label={
                    activeTab === 'activity'
                      ? notesSaving
                        ? 'Saving notes'
                        : notesSaveSucceeded
                          ? 'Notes saved'
                          : 'Save notes'
                      : activeTab === 'attributes'
                        ? behaviorSaving
                          ? 'Saving behaviors'
                          : behaviorSaveSucceeded
                            ? 'Behaviors saved'
                            : 'Save behaviors'
                        : activeTab === 'persona'
                          ? goalSaving
                            ? 'Saving goals'
                            : goalSaveSucceeded
                              ? 'Goals saved'
                              : 'Save goals'
                          : activeTab === 'voice'
                            ? voiceSaving
                              ? 'Saving voice'
                              : voiceSaveSucceeded
                                ? 'Voice saved'
                                : 'Save voice'
                            : empathySaving
                              ? 'Saving empathy map'
                              : empathySaveSucceeded
                                ? 'Empathy map saved'
                                : 'Save empathy map'
                  }
                >
                  {(activeTab === 'activity' && notesSaving) ||
                  (activeTab === 'attributes' && behaviorSaving) ||
                  (activeTab === 'persona' && goalSaving) ||
                  (activeTab === 'voice' && voiceSaving) ||
                  (activeTab === 'empathy' && empathySaving) ? (
                    <ReloadIcon
                      width={14}
                      height={14}
                      className="customer-notes__save-spinner"
                      aria-label="Saving"
                    />
                  ) : (activeTab === 'activity' && notesSaveSucceeded) ||
                    (activeTab === 'attributes' && behaviorSaveSucceeded) ||
                    (activeTab === 'persona' && goalSaveSucceeded) ||
                    (activeTab === 'voice' && voiceSaveSucceeded) ||
                    (activeTab === 'empathy' && empathySaveSucceeded) ? (
                    <CheckIcon width={14} height={14} aria-hidden="true" />
                  ) : (
                    'Save'
                  )}
                </Button>
                <div className="customer-notes__status-row">
                  <span
                    className={
                      (activeTab === 'activity' && notesSaveError) ||
                      (activeTab === 'attributes' && behaviorSaveError) ||
                      (activeTab === 'persona' && goalSaveError) ||
                      (activeTab === 'voice' && voiceSaveError) ||
                      (activeTab === 'empathy' && empathySaveError)
                        ? 'customer-notes__save-feedback customer-notes__save-feedback--error'
                        : 'customer-notes__save-feedback'
                    }
                    role="status"
                    aria-live="polite"
                  >
                    {activeTab === 'activity'
                      ? notesSaveError || ''
                      : activeTab === 'attributes'
                        ? behaviorSaveError || ''
                        : activeTab === 'persona'
                          ? goalSaveError || ''
                          : activeTab === 'voice'
                            ? voiceSaveError || ''
                            : activeTab === 'empathy'
                              ? empathySaveError || ''
                              : ''}
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          <div
            className={
              activeTab === 'activity'
                ? 'customer-detail__split-tab-content customer-detail__split-tab-content--notes'
                : 'customer-detail__split-tab-content'
            }
          >
            {activeTab === 'attributes' &&
              (behaviorSections.length > 0 ? (
                <div className="customer-detail__group-sections">
                  {behaviorSections.map((section) => {
                    const isMulti =
                      section.control === 'tags' || section.control === 'multi_select';
                    return (
                      <section key={section.key} className="customer-detail__group-section">
                        <div className="customer-detail__section-heading customer-detail__section-heading--inline-copy">
                          <h3 className="customer-detail__section-title">
                            {behaviorDirtyKeySet.has(section.key)
                              ? `${section.label}*`
                              : section.label}
                          </h3>
                          {section.description ? (
                            <p className="customer-detail__section-copy customer-detail__section-copy--inline">
                              {section.description}
                            </p>
                          ) : null}
                        </div>

                        {isMulti ? (
                          <TagInput
                            tags={behaviorDraftValues[section.key] ?? []}
                            onChange={(tags) => {
                              setBehaviorDraftValues((current) => ({
                                ...current,
                                [section.key]: tags,
                              }));
                              if (behaviorSaveError) {
                                setBehaviorSaveError(null);
                              }
                              if (behaviorSaveSucceeded) {
                                setBehaviorSaveSucceeded(false);
                              }
                            }}
                            aria-label={`${section.label} value`}
                          />
                        ) : (
                          <TextArea
                            value={(behaviorDraftValues[section.key] ?? []).join('')}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setBehaviorDraftValues((current) => ({
                                ...current,
                                [section.key]: [nextValue],
                              }));
                              if (behaviorSaveError) {
                                setBehaviorSaveError(null);
                              }
                              if (behaviorSaveSucceeded) {
                                setBehaviorSaveSucceeded(false);
                              }
                              autoGrowTextarea(event.currentTarget);
                            }}
                            ref={(target) => {
                              if (target) {
                                autoGrowTextarea(target);
                              }
                            }}
                            rows={3}
                            className="customer-detail__behavior-textarea"
                            aria-label={`${section.label} value`}
                          />
                        )}
                        {section.valueState === 'ai_suggested' &&
                        customerUuid &&
                        aiProvenanceEnabled ? (
                          <AiProvenanceBadge
                            onAccept={async () => {
                              await customersApi.confirmAttributeValue(
                                projectUuid,
                                customerUuid,
                                section.key
                              );
                              await reload();
                            }}
                            onReject={async () => {
                              await customersApi.rejectAttributeValue(
                                projectUuid,
                                customerUuid,
                                section.key
                              );
                              await reload();
                            }}
                          />
                        ) : null}
                      </section>
                    );
                  })}
                </div>
              ) : (
                <p className="customer-detail__empty-copy">
                  No behavior attributes configured in the template yet.
                </p>
              ))}

            {activeTab === 'persona' &&
              (goalSections.length > 0 ? (
                <div className="customer-detail__group-sections">
                  {goalSections.map((section) => (
                    <section key={section.key} className="customer-detail__group-section">
                      <div className="customer-detail__section-heading customer-detail__section-heading--inline-copy">
                        <h3 className="customer-detail__section-title">
                          {goalDirtyKeySet.has(section.key) ? `${section.label}*` : section.label}
                        </h3>
                        {section.description ? (
                          <p className="customer-detail__section-copy customer-detail__section-copy--inline">
                            {section.description}
                          </p>
                        ) : null}
                      </div>

                      <TextArea
                        value={goalDraftValues[section.key] ?? ''}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setGoalDraftValues((current) => ({
                            ...current,
                            [section.key]: nextValue,
                          }));
                          if (goalSaveError) {
                            setGoalSaveError(null);
                          }
                          if (goalSaveSucceeded) {
                            setGoalSaveSucceeded(false);
                          }
                          autoGrowTextarea(event.currentTarget);
                        }}
                        ref={(target) => {
                          if (target) {
                            autoGrowTextarea(target);
                          }
                        }}
                        rows={3}
                        className="customer-detail__behavior-textarea"
                        aria-label={`${section.label} value`}
                      />
                      {section.valueState === 'ai_suggested' &&
                      customerUuid &&
                      aiProvenanceEnabled ? (
                        <AiProvenanceBadge
                          onAccept={async () => {
                            await customersApi.confirmAttributeValue(
                              projectUuid,
                              customerUuid,
                              section.key
                            );
                            await reload();
                          }}
                          onReject={async () => {
                            await customersApi.rejectAttributeValue(
                              projectUuid,
                              customerUuid,
                              section.key
                            );
                            await reload();
                          }}
                        />
                      ) : null}
                    </section>
                  ))}
                </div>
              ) : (
                <p className="customer-detail__empty-copy">
                  No goals section values yet. AI or manual updates will appear here.
                </p>
              ))}

            {activeTab === 'voice' &&
              (voiceSections.length > 0 ? (
                <div className="customer-detail__group-sections">
                  {voiceSections.map((section) => {
                    const isMulti =
                      section.control === 'tags' || section.control === 'multi_select';
                    return (
                      <section key={section.key} className="customer-detail__group-section">
                        <div className="customer-detail__section-heading customer-detail__section-heading--inline-copy">
                          <h3 className="customer-detail__section-title">
                            {voiceDirtyKeySet.has(section.key)
                              ? `${section.label}*`
                              : section.label}
                          </h3>
                          {section.description ? (
                            <p className="customer-detail__section-copy customer-detail__section-copy--inline">
                              {section.description}
                            </p>
                          ) : null}
                        </div>

                        {isMulti ? (
                          <TagInput
                            tags={voiceDraftValues[section.key] ?? []}
                            onChange={(tags) => {
                              setVoiceDraftValues((current) => ({
                                ...current,
                                [section.key]: tags,
                              }));
                              if (voiceSaveError) {
                                setVoiceSaveError(null);
                              }
                              if (voiceSaveSucceeded) {
                                setVoiceSaveSucceeded(false);
                              }
                            }}
                            aria-label={`${section.label} value`}
                          />
                        ) : (
                          <TextArea
                            value={(voiceDraftValues[section.key] ?? []).join('')}
                            onChange={(event) => {
                              const nextValue = event.target.value;
                              setVoiceDraftValues((current) => ({
                                ...current,
                                [section.key]: [nextValue],
                              }));
                              if (voiceSaveError) {
                                setVoiceSaveError(null);
                              }
                              if (voiceSaveSucceeded) {
                                setVoiceSaveSucceeded(false);
                              }
                              autoGrowTextarea(event.currentTarget);
                            }}
                            ref={(target) => {
                              if (target) {
                                autoGrowTextarea(target);
                              }
                            }}
                            rows={3}
                            className="customer-detail__behavior-textarea"
                            aria-label={`${section.label} value`}
                          />
                        )}
                        {section.valueState === 'ai_suggested' &&
                        customerUuid &&
                        aiProvenanceEnabled ? (
                          <AiProvenanceBadge
                            onAccept={async () => {
                              await customersApi.confirmAttributeValue(
                                projectUuid,
                                customerUuid,
                                section.key
                              );
                              await reload();
                            }}
                            onReject={async () => {
                              await customersApi.rejectAttributeValue(
                                projectUuid,
                                customerUuid,
                                section.key
                              );
                              await reload();
                            }}
                          />
                        ) : null}
                      </section>
                    );
                  })}
                </div>
              ) : (
                <p className="customer-detail__empty-copy">
                  No voice attributes configured in the template yet.
                </p>
              ))}

            {activeTab === 'empathy' &&
              (empathySections.length > 0 ? (
                <div className="customer-detail__group-sections">
                  {empathySections.map((section) => (
                    <section key={section.key} className="customer-detail__group-section">
                      <div className="customer-detail__section-heading customer-detail__section-heading--inline-copy">
                        <h3 className="customer-detail__section-title">
                          {empathyDirtyKeySet.has(section.key)
                            ? `${section.label}*`
                            : section.label}
                        </h3>
                        {section.description ? (
                          <p className="customer-detail__section-copy customer-detail__section-copy--inline">
                            {section.description}
                          </p>
                        ) : null}
                      </div>

                      <TextArea
                        value={empathyDraftValues[section.key] ?? ''}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setEmpathyDraftValues((current) => ({
                            ...current,
                            [section.key]: nextValue,
                          }));
                          if (empathySaveError) {
                            setEmpathySaveError(null);
                          }
                          if (empathySaveSucceeded) {
                            setEmpathySaveSucceeded(false);
                          }
                          autoGrowTextarea(event.currentTarget);
                        }}
                        ref={(target) => {
                          if (target) {
                            autoGrowTextarea(target);
                          }
                        }}
                        rows={3}
                        className="customer-detail__behavior-textarea"
                        aria-label={`${section.label} value`}
                      />
                      {section.valueState === 'ai_suggested' &&
                      customerUuid &&
                      aiProvenanceEnabled ? (
                        <AiProvenanceBadge
                          onAccept={async () => {
                            await customersApi.confirmAttributeValue(
                              projectUuid,
                              customerUuid,
                              section.key
                            );
                            await reload();
                          }}
                          onReject={async () => {
                            await customersApi.rejectAttributeValue(
                              projectUuid,
                              customerUuid,
                              section.key
                            );
                            await reload();
                          }}
                        />
                      ) : null}
                    </section>
                  ))}
                </div>
              ) : (
                <p className="customer-detail__empty-copy">
                  No empathy map values yet. AI or manual updates will appear here.
                </p>
              ))}

            {activeTab === 'activity' && (
              <section className="customer-notes">
                <p className="customer-detail__section-copy customer-notes__description">
                  {notesTemplateAttribute?.description?.trim() ||
                    'Capture structured meeting notes, observations, and follow-ups for this customer.'}
                </p>
                <div className="customer-notes__body">
                  <RichTextEditor
                    content={notesBody}
                    onUpdate={(json, text) => {
                      setNotesBody(json);
                      setNotesText(text);
                      if (notesSaveError) {
                        setNotesSaveError(null);
                      }
                      if (notesSaveSucceeded) {
                        setNotesSaveSucceeded(false);
                      }
                    }}
                    minHeight="100%"
                    className="customer-notes__editor"
                    placeholder="Type / to view controls"
                    toolbar
                  />
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
